import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import Button from '../Button'
import Link from '../Link'
import ReleaseNotesList, { UnreadDot } from './ReleaseNotesList'
import ReleaseNoteDetail from './ReleaseNoteDetail'

// Utils
import { useAppState } from '../../context/AppContext'
import useReleaseNotes from '../../hooks/useReleaseNotes'
import useMediaQuery from '../../hooks/useMediaQuery'

// Types
import { ReleaseNoteAudience } from '../../common/models/ReleaseNote'

// Styles
import { hsla } from '../../styles/colors'
import { shadows } from '../../styles/effects'
import { mqFrom } from '../../styles/mediaQueries'
import { Breakpoint } from '../../styles/responsive'

interface IProps {
  initialNoteId?: string
  closeModal: () => void
}

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Centered "What's new" dialog: the selected note under a top bar, with the note
// list collapsed behind a toggle. Opened, the list sits next to the note from L
// up and replaces it in the smaller popup below that. Closes on Escape and
// backdrop click, and keeps focus inside the dialog.
const ReleaseNotesModal: React.FC<IProps> = ({ initialNoteId, closeModal }) => {
  const { t } = useTranslation()
  const { company } = useAppState()
  const clientLead = company?.clientLeads?.[0]
  const [viewAs, setViewAs] = useState<ReleaseNoteAudience>()
  const { audience, notes, unreadIds, open } = useReleaseNotes(false, viewAs)
  const [selectedId, setSelectedId] = useState(initialNoteId)
  const [listOpen, setListOpen] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const pressStartedInside = useRef(false)

  // Another role's view may not contain the selected note: fall back to newest
  const selected = notes.find((note) => note.id === selectedId) || notes[0]

  // Showing a note counts as opening it
  useEffect(() => {
    if (selected) open(selected.id)
  }, [selected?.id])

  useEffect(() => {
    dialogRef.current?.focus()
    const body = document.body
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = 'initial'
    }
  }, [])

  // Next to the note the list stays open until toggled; below L it replaces
  // the note, so picking one closes it
  const listBesideNote = useMediaQuery(`(min-width: ${Breakpoint.L / 16}em)`)
  const select = (noteId: string) => {
    setSelectedId(noteId)
    if (!listBesideNote) setListOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      closeModal()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
    ).filter((element) => element.offsetParent !== null)
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (!first || !last) return
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // Portaled to the body, so a parent hidden at another breakpoint (the settings
  // help block renders once per layout) can't hide the open dialog with it
  return ReactDOM.createPortal(
    <Overlay
      onMouseDown={(event) => {
        pressStartedInside.current = !!dialogRef.current?.contains(
          event.target as Node
        )
      }}
      onClick={() => {
        if (!pressStartedInside.current) closeModal()
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('releaseNotes.title')}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <TopBar>
          <ListToggle>
            <Button
              icon="List"
              colorVariant="secondary"
              ariaLabel={t('releaseNotes.allUpdates')}
              onClick={() => setListOpen(!listOpen)}
            />
            {!!unreadIds.length && <Badge />}
          </ListToggle>
          <Title textStyle="title" tag="h2">
            {t('releaseNotes.title')}
          </Title>
          {clientLead && (
            <Link
              caption={t('releaseNotes.contact')}
              icon="Mail"
              iconSize={16}
              color="primary"
              disableUnderline
              onClick={() => {
                window.location.href = `mailto:${clientLead.email}`
              }}
            />
          )}
          <Button
            icon="Close"
            colorVariant="secondary"
            ariaLabel={t('general.actions.close')}
            onClick={closeModal}
          />
        </TopBar>
        <Body>
          <ReleaseNotesList
            audience={audience}
            notes={notes}
            unreadIds={unreadIds}
            selectedId={selected?.id}
            isOpen={listOpen}
            onSelect={select}
            onChangeAudience={setViewAs}
          />
          {selected && (
            <ReleaseNoteDetail
              audience={audience}
              note={selected}
              isHidden={listOpen}
            />
          )}
        </Body>
      </Dialog>
    </Overlay>,
    document.body
  )
}

export default ReleaseNotesModal

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: ${({ theme }) => hsla(theme.colors.text, 0.4)};
  animation: ${fadeIn} 240ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${mqFrom.M`
    padding: 24px;
  `}
`

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 480px;
  max-height: 640px;
  overflow: hidden;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthFive};
  outline: none;
  animation: ${rise} 320ms cubic-bezier(0.16, 1, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${mqFrom.L`
    max-width: 1080px;
    max-height: 740px;
  `}
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  height: 64px;
  padding: 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.textLightest};

  ${mqFrom.L`
    padding: 0 24px;
  `}
`

const ListToggle = styled.div`
  position: relative;
  flex-shrink: 0;
`

// Rim in the dialog color so the badge reads as sitting on the button
const Badge = styled(UnreadDot)`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border: 2px solid ${({ theme }) => theme.colors.lightest};
`

const Title = styled(Text)`
  flex-grow: 1;
  min-width: 0;
`

const Body = styled.div`
  display: flex;
  flex-grow: 1;
  min-height: 0;
`
